import React, {useState} from 'react';
import {Box, Collapse, Divider, IconButton, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ManageExecution} from '@store/operations/executions';

import {Colors} from '@constants/colors.constants';
import {Alert, ChevronDown, Close, Comment, Location} from '@icons/index';

import ClientDetailsForm from '@screens/admin/operations/executions/manageExecution/components/ClientDetails.form';
import DetailsForm from '@screens/admin/operations/executions/manageExecution/components/Details.form';
import AvailabilityRulesForm
  from '@screens/admin/operations/executions/manageExecution/components/AvailabilityRules.form';
import DocumentsForm from '@screens/admin/operations/executions/manageExecution/components/Documents.form';
import CommentsForm from '@screens/admin/operations/executions/manageExecution/components/Comments.form';
import IssueForm from '@screens/admin/operations/executions/manageExecution/components/Issue.form';
import StatusHistoryComponent
  from '@screens/admin/operations/executions/manageExecution/components/StatusHistory.component';
import ExecutionDistributionRules
  from '@screens/admin/operations/executions/manageExecution/components/ExecutionDistributionRules';

interface ExecutionCardProps {
  errors?: { [key: string]: any };
  execution: ManageExecution;
  handleDeleteExecution?: () => void;
  isMainExecution: boolean;
  readOnly?: boolean;
  updateExecution: (data: ManageExecution, field?: string) => void;
}

const ExecutionCard = (props: ExecutionCardProps) => {
  const {errors = {}, execution, handleDeleteExecution, isMainExecution, readOnly, updateExecution} = props;

  const {t} = useTranslation();

  const [expanded, setExpanded] = useState(true);

  return (
    <Stack bgcolor={Colors.background}>
      <Box onClick={() => setExpanded(!expanded)} sx={{cursor: 'pointer'}}>
        <Stack pb="8px" pt="30px" px="16px">
          <Stack direction="row" mb="20px" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {!!handleDeleteExecution && execution.isNew && (
                <IconButton
                  sx={{ml: '-8px'}}
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleDeleteExecution();
                  }}>
                  <Close style={{height: 15, width: 15}}/>
                </IconButton>
              )}
              {!execution.isNew && !!execution.author ? (
                <Typography fontSize={12} fontWeight="400" color={Colors.textWithOpacity}>
                  {t('global.createdBy', {
                    by: {firstName: execution.author?.firstName, lastName: execution.author?.lastName[0]},
                  })}
                </Typography>
              ) : <Box/>}
            </Stack>
            <ChevronDown
              style={{transform: expanded ? 'rotate(180deg)' : undefined}}/>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography fontSize={24} fontWeight="700" flex={1}>
              {t(`operations.executions.manage.executionTitle.${execution.isNew ? 'add' : 'edit'}`, {ticket: execution.ticketSignature})}
            </Typography>
            {(execution.issuesCount || 0) > 0 && (
              <Alert style={{height: 20, width: 20}}/>
            )}
            {(execution.commentsCount || 0) > 0 && (
              <Comment style={{height: 20, width: 20}}/>
            )}
          </Stack>
          {!!execution.organisationUnit?.address && isMainExecution && (
            <Stack direction="row" mt="3px" alignItems="center" spacing={0.5}>
              <Location/>
              <Typography fontSize={12} fontWeight="400" color={Colors.textWithOpacity}>
                {execution.organisationUnit.address.street}, {execution.organisationUnit.address.zip} {execution.organisationUnit.address.city}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
      <Collapse in={expanded}>
        <Stack px="16px">
          <Divider variant="fullWidth" orientation="horizontal"/>
          <Stack pb="10px">
            {isMainExecution && (
              <ClientDetailsForm
                readOnly={readOnly}
                isCreation={execution.isNew}
                execution={execution}
                errors={errors}
                updateExecution={updateExecution}/>
            )}
            <DetailsForm
              readOnly={readOnly}
              isCreation={execution.isNew}
              isMainExecution={isMainExecution}
              execution={execution}
              errors={errors}
              updateExecution={updateExecution}/>
            {!!execution.recurrence && (
              <AvailabilityRulesForm/>
            )}
            <DocumentsForm readOnly={readOnly} execution={execution} updateExecution={updateExecution}/>
            {!execution.isNew && (
              <>
                <CommentsForm readOnly={readOnly} executionId={execution.id!}/>
                <IssueForm readOnly={readOnly} executionId={execution.id!}/>
              </>
            )}
            {
              execution.isFromGenericUnit && execution.organisation?.id &&
                <ExecutionDistributionRules organisationId={execution?.organisation?.id} onChange={(dRules) => {
                  updateExecution({ distributionRules: dRules });
                }} errors={errors} distributionRules={execution.distributionRules} />
            }
            {!execution.isNew && isMainExecution && (
              <StatusHistoryComponent execution={execution}/>
            )}

          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default ExecutionCard;
